import i18next from "i18next";
import { useMemo, useState } from "react";
import { Box, Paper, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import {
  aggregateMatchday, formatGoalMetric, formatOutcomeMetric,
  getAverageGoalDeviation, getAverageProbabilities,
  getFixtureDifferences, getFixtureGoalLayers,
} from "../helpers/leagueMatchdayAnalysis";

const layers = ["actual", "expected", "should"];
const text = (key, values) =>
  i18next.t(`help.leagueAnalysis.matchdayComparison.${key}`, values);
const formatPair = (value) =>
  value ? `${value.home.toFixed(2)} : ${value.away.toFixed(2)}` : "–";
const signed = (value) =>
  Number.isFinite(value) ? `${value > 0 ? "+" : ""}${value.toFixed(2)}` : "–";
const percent = (value) =>
  Number.isFinite(value) ? `${(value * 100).toFixed(1)} %` : "–";

export function LeagueFixtureGoalAnalysis({ fixture }) {
  const goalLayers = getFixtureGoalLayers(fixture);
  const differences = getFixtureDifferences(fixture);
  const tooltip = [
    `${text("expectedDiff")} ${signed(differences.expectedDiff)}`,
    `${text("actualDiff")} ${signed(differences.actualDiff)}`,
    `${text("shouldDiff")} ${signed(differences.shouldDiff)}`,
    `${text("performanceVsExpected")} ${signed(differences.performanceVsExpected)}`,
    `${text("resultVsExpected")} ${signed(differences.resultVsExpected)}`,
  ].join("\n");

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr)) 110px", gap: 0.5 }}>
      {["expected", "should"].map((key) => (
        <Typography key={key} variant="body2" noWrap align="center" sx={{ px: 0.45, py: 0.48, bgcolor: "action.hover", borderRadius: 0.5, fontSize: "0.76rem", fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>
          {formatPair(goalLayers[key])}
        </Typography>
      ))}
      <Typography title={tooltip} variant="body2" align="center" sx={{ px: 0.45, py: 0.48, bgcolor: "rgba(126, 53, 88, 0.10)", borderRadius: 0.5, fontSize: "0.76rem", fontWeight: 900, color: "primary.main", fontVariantNumeric: "tabular-nums" }}>
        {signed(differences.performanceIndex)}
      </Typography>
    </Box>
  );
}

export function LeagueMatchdayComparison({ fixtures }) {
  const [section, setSection] = useState("goals");
  const [goalDisplay, setGoalDisplay] = useState("average");
  const [outcomeDisplay, setOutcomeDisplay] = useState("percent");
  const summaries = useMemo(() => aggregateMatchday(fixtures), [fixtures]);
  const expectedProbabilities = getAverageProbabilities(summaries.expected);
  const shouldProbabilities = getAverageProbabilities(summaries.should);

  function cardValue(key) {
    const summary = summaries[key];
    if (section === "goals") {
      const value = formatGoalMetric(summary, goalDisplay);
      if (value === "–") return value;
      if (goalDisplay === "average") return text("goalsPerMatch", { value });
      if (["total", "home-away"].includes(goalDisplay)) return text("goalsValue", { value });
      return value;
    }
    if (section === "probabilities") {
      if (key === "actual") return percent(expectedProbabilities.occurred);
      if (key === "expected") return percent(expectedProbabilities.expected);
      return percent(shouldProbabilities.expected);
    }
    if (section === "goal-deviation") {
      const value = getAverageGoalDeviation(summary);
      return Number.isFinite(value)
        ? text("deviationGoals", { value: value.toFixed(3) })
        : "–";
    }
    return null;
  }

  const firstLevel = [
    ["goals", "goals"], ["1x2", "oneXTwo"],
    ["probabilities", "probabilities"], ["goal-deviation", "goalDeviation"],
  ];
  const goalOptions = [
    ["total", "total"], ["average", "average"], ["home-away", "homeAway"],
    ["home-share", "homeShare"], ["home-factor", "homeFactor"],
  ];
  const outcomeOptions = [
    ["total", "count"], ["percent", "percent"], ["odds", "fairOdds"],
  ];

  return (
    <Paper variant="outlined" sx={{ mx: 1, my: 1, p: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.65, fontWeight: 900 }}>{text("title")}</Typography>
      <Box sx={{ minHeight: 70 }}>
        <ToggleButtonGroup exclusive size="small" value={section} onChange={(event, value) => value && setSection(value)}>
          {firstLevel.map(([value, label]) => (
            <ToggleButton key={value} value={value} sx={{ py: 0.2, textTransform: "none" }}>{text(label)}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Box sx={{ mt: 0.6, minHeight: 32 }}>
          {section === "goals" && (
            <ToggleButtonGroup exclusive size="small" value={goalDisplay} onChange={(event, value) => value && setGoalDisplay(value)}>
              {goalOptions.map(([value, label]) => <ToggleButton key={value} value={value} sx={{ py: 0.2, textTransform: "none" }}>{text(label)}</ToggleButton>)}
            </ToggleButtonGroup>
          )}
          {section === "1x2" && (
            <ToggleButtonGroup exclusive size="small" value={outcomeDisplay} onChange={(event, value) => value && setOutcomeDisplay(value)}>
              {outcomeOptions.map(([value, label]) => <ToggleButton key={value} value={value} sx={{ py: 0.2, textTransform: "none" }}>{text(label)}</ToggleButton>)}
            </ToggleButtonGroup>
          )}
        </Box>
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${section === "goal-deviation" ? 2 : 3}, minmax(0, 1fr))`, gap: 0.75 }}>
        {(section === "goal-deviation" ? ["actual", "expected"] : layers).map((key) => {
          const summary = summaries[key];
          return (
            <Box key={key} sx={{ minHeight: 68, p: 0.75, bgcolor: key === "expected" ? "rgba(126, 53, 88, 0.06)" : "action.hover", borderRadius: 0.75 }}>
              <Typography variant="caption" display="block" sx={{ mb: 0.35, fontWeight: 900 }}>
                {text(key)} · {text("matches", {
                  count: section === "goal-deviation"
                    ? summary.goalDeviationFixtures
                    : summary.fixtures,
                })}
              </Typography>
              {section === "1x2" ? (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 0.3 }}>
                  {[["homeWins", "1"], ["draws", "X"], ["awayWins", "2"]].map(([outcome, label]) => (
                    <Box key={outcome} sx={{ textAlign: "center" }}>
                      <Typography variant="caption" display="block" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>{formatOutcomeMetric(summary, outcome, outcomeDisplay)}</Typography>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 900, fontVariantNumeric: "tabular-nums" }}>{cardValue(key)}</Typography>
              )}
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}
